import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { Subscriber } from 'types/electionService';
import SubscriberBasicInfoDelegate from 'store/modules/subscriber/subscriber-basic-info/subscriberBasicInfoDelegate';
import EmployeeProfileTab from './employeeProfileTab';
import DependentsTab from './dependentsTab';
import { BaseFullEmployeeFormDomainParams, TabId } from './types';
import { syncSubscriberIdToUrl } from './utils';

export interface IFullFormPageStoreShape {
  readonly subscriberBasicInfo: SubscriberBasicInfoDelegate | undefined;
  readonly employeeProfileTab: EmployeeProfileTab;
  readonly dependentsTab: DependentsTab;
  readonly subscriberExists: boolean;
  readonly isInitialDataLoading: boolean;
}

export default abstract class IFullFormPageStore<
  DomainParams extends BaseFullEmployeeFormDomainParams,
  TTabId extends string = TabId,
> implements IFullFormPageStoreShape
{
  protected _domainData: DomainParams;
  protected _currentTabId: TTabId = TabId.EMPLOYEE_PROFILE as TTabId;
  protected _isInitialDataLoading = true;
  protected _isLoading = false;
  protected _subscriberBasicInfo: SubscriberBasicInfoDelegate | undefined = undefined;
  protected _employeeProfileTab: EmployeeProfileTab;
  protected _dependentsTab: DependentsTab;

  /** Load BE subscriber data; base maps it into {@link subscriberBasicInfo}. */
  protected abstract loadSubscriber(subscriberId: string): Promise<Pick<Subscriber, 'id' | 'common' | 'custom'> | null>;

  protected abstract getPrimaryFormId(): string | undefined;
  protected abstract getDependentFormId(): string | undefined;
  public abstract onBackRedirect(): void;

  public get subscriberBasicInfo(): SubscriberBasicInfoDelegate | undefined {
    return this._subscriberBasicInfo;
  }

  public get domainData(): DomainParams {
    return this._domainData;
  }

  public get currentTabId(): TTabId {
    return this._currentTabId;
  }

  protected static isValidTabId(value: string): boolean {
    return Object.values(TabId).includes(value as TabId);
  }

  public get employeeProfileTab(): EmployeeProfileTab {
    return this._employeeProfileTab;
  }

  public get dependentsTab(): DependentsTab {
    return this._dependentsTab;
  }

  public get subscriberExists(): boolean {
    return this.subscriberBasicInfo !== undefined;
  }

  public get aggregatedErrors(): string[] {
    return this._employeeProfileTab.errors;
  }

  public get isInitialDataLoading(): boolean {
    return this._isInitialDataLoading;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  protected _setIsInitialDataLoading(isLoading: boolean): void {
    this._isInitialDataLoading = isLoading;
  }

  protected _setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  protected async fetchSubscriberById(subscriberId: string): Promise<void> {
    const subscriber = await this.loadSubscriber(subscriberId);
    runInAction(() => {
      this._subscriberBasicInfo = subscriber
        ? SubscriberBasicInfoDelegate.fromSubscriber(subscriber, this._domainData.periodId)
        : undefined;
    });
  }

  public async onAddAnotherEmployee(): Promise<void> {
    this._currentTabId = TabId.EMPLOYEE_PROFILE as TTabId;
    this._domainData.subscriberId = undefined;
    this._subscriberBasicInfo = undefined;
    await this.employeeProfileTab.fetchForm();
  }

  public setCurrentTabId(tabId: string): void {
    if (!(this.constructor as typeof IFullFormPageStore).isValidTabId(tabId)) {
      throw new Error(`Invalid tab ID: ${tabId}`);
    }
    this._currentTabId = tabId as TTabId;
  }

  public async getInitialData(): Promise<void> {
    this._setIsInitialDataLoading(true);
    await runInAction(async () => {
      if (this._domainData.subscriberId) {
        await this.fetchSubscriberById(this._domainData.subscriberId);
      }
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });
    runInAction(() => {
      this._setIsInitialDataLoading(false);
    });
  }

  public async updateSubscriberData(): Promise<void> {
    if (!this._domainData.subscriberId) return;
    this._setIsLoading(true);
    await this.fetchSubscriberById(this._domainData.subscriberId);
    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public async setupPageForNewEmployee(subscriberId: string): Promise<void> {
    this._setIsInitialDataLoading(true);
    await runInAction(async () => {
      await this.fetchSubscriberById(subscriberId);
    });
    await runInAction(async () => {
      await this.employeeProfileTab.fetchForm();
    });

    runInAction(() => {
      this._domainData.subscriberId = subscriberId;
      syncSubscriberIdToUrl(subscriberId);
      this._setIsInitialDataLoading(false);
    });
  }

  constructor(domainData: DomainParams) {
    this._domainData = domainData;

    if (this._domainData.activeTabId) {
      if (!(this.constructor as typeof IFullFormPageStore).isValidTabId(this._domainData.activeTabId)) {
        throw new Error(`Invalid tab ID: ${this._domainData.activeTabId}`);
      }
      this._currentTabId = this._domainData.activeTabId as TTabId;
    }

    this._employeeProfileTab = new EmployeeProfileTab(this._domainData, this, () => this.getPrimaryFormId());
    this._dependentsTab = new DependentsTab(this, () => this.getDependentFormId());

    makeObservable<
      IFullFormPageStore<DomainParams, TTabId>,
      | '_domainData'
      | '_currentTabId'
      | '_isLoading'
      | '_isInitialDataLoading'
      | '_subscriberBasicInfo'
      | '_setIsInitialDataLoading'
      | '_setIsLoading'
    >(this, {
      _domainData: observable,
      _currentTabId: observable,
      _isInitialDataLoading: observable,
      _isLoading: observable,
      _subscriberBasicInfo: observable,
      currentTabId: computed,
      domainData: computed,
      subscriberBasicInfo: computed,
      subscriberExists: computed,
      aggregatedErrors: computed,
      isInitialDataLoading: computed,
      isLoading: computed,
      _setIsInitialDataLoading: action.bound,
      _setIsLoading: action.bound,
      setCurrentTabId: action.bound,
      onAddAnotherEmployee: action.bound,
      getInitialData: action.bound,
      updateSubscriberData: action.bound,
      setupPageForNewEmployee: action.bound,
    });
  }
}
